// 상대 시간(방금 전, n분 전, n시간 전 등) 한글 반환 유틸
export function getRelativeTime(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diff = (now.getTime() - date.getTime()) / 1000; // 초 단위
  if (diff < 60) return '방금 전';
  const min = Math.floor(diff / 60);
  if (min < 60) return `${min}분 전`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour}시간 전`;
  const day = Math.floor(hour / 24);
  if (day < 30) return `${day}일 전`;
  const month = Math.floor(day / 30);
  if (month < 12) return `${month}개월 전`;
  const year = Math.floor(month / 12);
  return `${year}년 전`;
}

/**
 * 날짜·시각 표시를 한곳에 모은다.
 *
 * 왜 직접 조립하나: `toLocaleString('ko-KR')` 은 `08. 10. 16:52:44` 처럼 점을 찍어서
 * 사이트의 다른 날짜 표시(`08/10`)와 어긋난다.
 *
 * 서버는 시각을 ISO(Z)로 준다(`"battledAt":"2026-08-10T07:52:44.000Z"`). 그래서
 * `getHours()` 같은 **지역시 함수**를 쓰면 보는 사람의 시간대로 알아서 맞는다.
 *
 * 전투기록은 **초까지** 보여준다. 게임이 초 단위로 주고(`log_timestamp`), 같은 분에
 * 여러 번 치는 일이 흔하다 — 실측으로 한 점령전 580건이 서로 다른 초 575개에
 * 흩어져 있었다. 분까지만 보면 연속 공격을 구별할 수 없다.
 */

const p2 = (n: number) => String(n).padStart(2, '0');

const parse = (value?: string | number | Date | null) => {
    if (value === undefined || value === null || value === '') return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

/** `08/10` */
export const monthDay = (value?: string | number | Date | null) => {
    const d = parse(value);
    return d ? `${p2(d.getMonth() + 1)}/${p2(d.getDate())}` : '';
};

/** `16:52:44` (초를 빼면 `16:52`) */
export const clockTime = (value?: string | number | Date | null, withSeconds = true) => {
    const d = parse(value);
    if (!d) return '';
    const hm = `${p2(d.getHours())}:${p2(d.getMinutes())}`;
    return withSeconds ? `${hm}:${p2(d.getSeconds())}` : hm;
};

/** `08/10 16:52:44` — 목록·표에서 쓰는 기본 꼴 */
export const monthDayTime = (value?: string | number | Date | null, withSeconds = true) => {
    const d = parse(value);
    return d ? `${monthDay(d)} ${clockTime(d, withSeconds)}` : '';
};

/** 전투기록 미리보기 카드 판 — 공유 주소 끝 `?v=` 에 붙는다. 서버 `backend/src/utils/battleCard.ts` 의 CARD_VER 와 같게 */
export const BATTLE_CARD_VER = 10;

/**
 * 게시물 하나가 전투기록인지.
 *
 * 서버가 전투기록을 게시물 모양으로 내려줄 때 `source: 'swex'` 를 붙인다
 * (`/api/siege-battles/as-posts`, `SiegeBattleList`). 이 표시가 있는 것만 초까지 쓴다 —
 * 사람이 쓴 글에 초까지 적을 이유는 없다.
 */
export const isBattleRecord = (post?: { source?: string } | null) => post?.source === 'swex';
